import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { ImageLightbox } from '../components/ImageLightbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FotoUploader } from '../components/FotoUploader';
import { 
  Users, 
  Plus, 
  Trash2, 
  Map, 
  ShoppingBag, 
  Calendar,
  MapPin,
  ExternalLink,
  Heart,
  MessageCircle,
  Globe,
  ZoomIn,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { ListSkeleton, CommunityPostSkeleton } from '../components/Skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAESI = [
  { code: 'ALL', name: 'Tutti i paesi' },
  { code: 'CH', name: 'Svizzera' },
  { code: 'IT', name: 'Italia' },
  { code: 'DE', name: 'Germania' },
  { code: 'FR', name: 'Francia' },
  { code: 'AT', name: 'Austria' },
  { code: 'ES', name: 'Spagna' },
];

const REGIONI = {
  CH: ['Ticino', 'Zurigo', 'Berna', 'Ginevra', 'Vallese', 'Grigioni', 'Lucerna', 'Basilea', 'San Gallo', 'Altro'],
  IT: ['Lombardia', 'Piemonte', 'Veneto', 'Emilia-Romagna', 'Toscana', 'Lazio', 'Campania', 'Sicilia', 'Sardegna', 'Altro'],
  DE: ['Baviera', 'Baden-Württemberg', 'Renania', 'Berlino', 'Amburgo', 'Altro'],
  FR: ['Île-de-France', 'Provenza', 'Alpi', 'Bretagna', 'Normandia', 'Altro'],
  AT: ['Vienna', 'Tirolo', 'Salisburgo', 'Carinzia', 'Altro'],
  ES: ['Catalogna', 'Madrid', 'Andalusia', 'Valencia', 'Altro'],
};

export default function Community() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('viaggi');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterPaese, setFilterPaese] = useState('ALL');
  const [filterRegione, setFilterRegione] = useState('__all__');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Post detail dialog
  const [selectedPost, setSelectedPost] = useState(null);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const [formData, setFormData] = useState({
    tipo: 'viaggi',
    titolo: '',
    descrizione: '',
    paese: user?.paese || 'CH',
    regione: '',
    link: '',
    data_evento: '',
    foto_urls: []
  });

  useEffect(() => {
    loadItems();
  }, [activeTab, filterPaese, filterRegione]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = { tipo: activeTab };
      if (filterPaese && filterPaese !== 'ALL') params.paese = filterPaese;
      if (filterRegione && filterRegione !== '__all__') params.regione = filterRegione;
      
      const response = await api.get('/community', { params });
      setItems(response.data);
    } catch (error) {
      console.error('Error loading community:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: activeTab,
      titolo: '',
      descrizione: '',
      paese: user?.paese || 'CH',
      regione: '',
      link: '',
      data_evento: '',
      foto_urls: []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.titolo || !formData.descrizione) {
      toast.error(t('community.fillRequired'));
      return;
    }

    try {
      await api.post('/community', { ...formData, tipo: activeTab });
      toast.success(t('success.saved'));
      setDialogOpen(false);
      resetForm();
      loadItems();
    } catch (error) {
      toast.error(t('errors.saveError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('community.deleteConfirm'))) return;
    
    try {
      await api.delete(`/community/${id}`);
      toast.success(t('success.deleted'));
      loadItems();
    } catch (error) {
      toast.error(t('errors.deleteError'));
    }
  };

  const handleLike = async (id) => {
    try {
      await api.post(`/community/${id}/like`);
      loadItems();
    } catch (error) {
      console.error('Error liking:', error);
    }
  };

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getImageUrl = (url) => {
    return url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const getTabIcon = (tab) => {
    switch(tab) {
      case 'viaggi': return <Map className="w-4 h-4" />;
      case 'shopping': return <ShoppingBag className="w-4 h-4" />;
      case 'raduni': return <Calendar className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  // Open post detail
  const openPostDetail = async (post) => {
    setSelectedPost(post);
    setPostDialogOpen(true);
    loadComments(post.id);
  };

  // Load comments for a post
  const loadComments = async (postId) => {
    setLoadingComments(true);
    try {
      const response = await api.get(`/community/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error loading comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  // Submit new comment
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedPost) return;

    setSubmittingComment(true);
    try {
      await api.post(`/community/${selectedPost.id}/comments`, { testo: newComment });
      setNewComment('');
      loadComments(selectedPost.id);
      // Update comment count in items list
      setItems(prev => prev.map(item => 
        item.id === selectedPost.id 
          ? { ...item, comments_count: (item.comments_count || 0) + 1 }
          : item
      ));
      toast.success(t('community.commentAdded') || 'Commento aggiunto');
    } catch (error) {
      toast.error(t('errors.saveError'));
    } finally {
      setSubmittingComment(false);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    if (!selectedPost) return;
    try {
      await api.delete(`/community/${selectedPost.id}/comments/${commentId}`);
      loadComments(selectedPost.id);
      setItems(prev => prev.map(item => 
        item.id === selectedPost.id 
          ? { ...item, comments_count: Math.max(0, (item.comments_count || 0) - 1) }
          : item
      ));
      toast.success(t('success.deleted'));
    } catch (error) {
      toast.error(t('errors.deleteError'));
    }
  };

  // Format date for comments
  const formatCommentDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const availableRegions = filterPaese && filterPaese !== 'ALL' ? REGIONI[filterPaese] || [] : [];
  const formRegions = formData.paese ? REGIONI[formData.paese] || [] : [];

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Users className="w-8 h-8 text-[#FF5F1F]" />
              {t('nav.community')}
            </h1>
            <p className="page-subtitle">{t('community.subtitle')}</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <TabsList className="bg-[#121212] border border-[#262626] p-1">
              <TabsTrigger 
                value="viaggi" 
                className="data-[state=active]:bg-[#FF5F1F] data-[state=active]:text-white flex items-center gap-2"
              >
                <Map className="w-4 h-4" />
                {t('community.tabs.trips')}
              </TabsTrigger>
              <TabsTrigger 
                value="shopping"
                className="data-[state=active]:bg-[#FF5F1F] data-[state=active]:text-white flex items-center gap-2"
              >
                <ShoppingBag className="w-4 h-4" />
                {t('community.tabs.shopping')}
              </TabsTrigger>
              <TabsTrigger 
                value="raduni"
                className="data-[state=active]:bg-[#FF5F1F] data-[state=active]:text-white flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                {t('community.tabs.events')}
              </TabsTrigger>
            </TabsList>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={resetForm}
                  className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold"
                  data-testid="add-community-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('community.share')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Teko'] text-2xl text-white uppercase flex items-center gap-2">
                    {getTabIcon(activeTab)}
                    {t(`community.add.${activeTab}`)}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="form-label">{t('community.form.title')} *</Label>
                    <Input
                      value={formData.titolo}
                      onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="community-titolo"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">{t('auth.country')} *</Label>
                      <Select 
                        value={formData.paese} 
                        onValueChange={(v) => setFormData({...formData, paese: v, regione: ''})}
                      >
                        <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                          {PAESI.filter(p => p.code !== 'ALL').map(p => (
                            <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label">{t('community.form.region')}</Label>
                      <Select 
                        value={formData.regione} 
                        onValueChange={(v) => setFormData({...formData, regione: v})}
                      >
                        <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11">
                          <SelectValue placeholder={t('community.form.selectRegion')} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                          {formRegions.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">{t('community.form.description')} *</Label>
                    <Textarea
                      value={formData.descrizione}
                      onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none min-h-[100px]"
                    />
                  </div>

                  {activeTab === 'shopping' && (
                    <div>
                      <Label className="form-label">{t('community.form.link')}</Label>
                      <Input
                        type="url"
                        value={formData.link}
                        onChange={(e) => setFormData({...formData, link: e.target.value})}
                        placeholder="https://"
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                      />
                    </div>
                  )}

                  {activeTab === 'raduni' && (
                    <div>
                      <Label className="form-label">{t('community.form.eventDate')}</Label>
                      <Input
                        type="date"
                        value={formData.data_evento}
                        onChange={(e) => setFormData({...formData, data_evento: e.target.value})}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      />
                    </div>
                  )}

                  <div>
                    <Label className="form-label">{t('moto.photos')}</Label>
                    <FotoUploader
                      fotoUrls={formData.foto_urls}
                      onChange={(urls) => setFormData({...formData, foto_urls: urls})}
                      maxFiles={5}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1 border-[#262626] hover:bg-[#262626]"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
                    >
                      {t('community.share')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6 p-4 bg-[#121212] border border-[#262626]">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#9CA3AF]" />
              <span className="text-[#9CA3AF] text-sm">{t('community.filters')}:</span>
            </div>
            <Select value={filterPaese} onValueChange={(v) => { setFilterPaese(v); setFilterRegione(''); }}>
              <SelectTrigger className="w-40 bg-black/50 border-[#262626] rounded-none h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                {PAESI.map(p => (
                  <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableRegions.length > 0 && (
              <Select value={filterRegione} onValueChange={setFilterRegione}>
                <SelectTrigger className="w-40 bg-black/50 border-[#262626] rounded-none h-9 text-sm">
                  <SelectValue placeholder={t('community.form.allRegions')} />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                  <SelectItem value="__all__">{t('community.form.allRegions')}</SelectItem>
                  {availableRegions.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Content */}
          {['viaggi', 'shopping', 'raduni'].map(tab => (
            <TabsContent key={tab} value={tab}>
              {loading ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="list-item" style={{ animationDelay: `${i * 0.05}s` }}>
                      <CommunityPostSkeleton />
                    </div>
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="vault-card text-center py-12">
                  {getTabIcon(tab)}
                  <p className="text-[#9CA3AF] text-lg mb-2 mt-4">{t(`community.empty.${tab}`)}</p>
                  <p className="text-[#6B7280] text-sm">{t('community.beFirst')}</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((item) => (
                    <div 
                      key={item.id} 
                      className="vault-card p-4 cursor-pointer hover:border-[#FF5F1F]/50 transition-colors" 
                      data-testid={`community-item-${item.id}`}
                      onClick={() => openPostDetail(item)}
                    >
                      {/* Image */}
                      {item.foto_urls && item.foto_urls.length > 0 && (
                        <div 
                          className="w-full h-40 mb-4 bg-[#121212] border border-[#262626] overflow-hidden relative group"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLightbox(item.foto_urls, 0);
                          }}
                        >
                          <img 
                            src={getImageUrl(item.foto_urls[0])} 
                            alt={item.titolo}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <ZoomIn className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      )}
                      
                      {/* Location badge */}
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-3 h-3 text-[#FF5F1F]" />
                        <span className="text-[#9CA3AF] text-xs">
                          {PAESI.find(p => p.code === item.paese)?.name || item.paese}
                          {item.regione && ` • ${item.regione}`}
                        </span>
                      </div>

                      {/* Title & Description */}
                      <h3 className="text-white font-semibold mb-2">{item.titolo}</h3>
                      <p className="text-[#9CA3AF] text-sm line-clamp-3 mb-3">{item.descrizione}</p>

                      {/* Event date for raduni */}
                      {item.data_evento && (
                        <p className="text-[#FF5F1F] text-sm mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {item.data_evento}
                        </p>
                      )}

                      {/* Link for shopping */}
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#FF5F1F] text-sm mb-3 flex items-center gap-2 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                          {t('community.visitSite')}
                        </a>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-3 border-t border-[#262626]">
                        <div className="flex items-center gap-4">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLike(item.id);
                            }}
                            className="flex items-center gap-1 text-[#9CA3AF] hover:text-[#FF5F1F] transition-colors"
                          >
                            <Heart className={`w-4 h-4 ${item.likes?.includes(user?.id) ? 'fill-[#FF5F1F] text-[#FF5F1F]' : ''}`} />
                            <span className="text-sm">{item.likes?.length || 0}</span>
                          </button>
                          <div className="flex items-center gap-1 text-[#9CA3AF]">
                            <MessageCircle className="w-4 h-4" />
                            <span className="text-sm">{item.comments_count || 0}</span>
                          </div>
                        </div>
                        <span className="text-[#6B7280] text-xs">
                          {item.user_nome || t('community.anonymous')}
                        </span>
                        {item.user_id === user?.id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id);
                            }}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {/* Post Detail Dialog */}
      <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPost && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-xl">{selectedPost.titolo}</DialogTitle>
                <div className="flex items-center gap-2 text-[#9CA3AF] text-sm">
                  <MapPin className="w-3 h-3 text-[#FF5F1F]" />
                  {PAESI.find(p => p.code === selectedPost.paese)?.name || selectedPost.paese}
                  {selectedPost.regione && ` • ${selectedPost.regione}`}
                  <span className="ml-auto">{selectedPost.user_nome}</span>
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Images */}
                {selectedPost.foto_urls && selectedPost.foto_urls.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedPost.foto_urls.map((url, idx) => (
                      <img
                        key={idx}
                        src={getImageUrl(url)}
                        alt={`${selectedPost.titolo} ${idx + 1}`}
                        className="w-full h-40 object-cover cursor-pointer border border-[#262626]"
                        onClick={() => openLightbox(selectedPost.foto_urls, idx)}
                      />
                    ))}
                  </div>
                )}

                {/* Description */}
                <p className="text-[#d4d4d4] whitespace-pre-wrap">{selectedPost.descrizione}</p>

                {/* Event date */}
                {selectedPost.data_evento && (
                  <p className="text-[#FF5F1F] flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {selectedPost.data_evento}
                  </p>
                )}

                {/* Link */}
                {selectedPost.link && (
                  <a
                    href={selectedPost.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FF5F1F] flex items-center gap-2 hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('community.visitSite')}
                  </a>
                )}

                {/* Like button */}
                <div className="flex items-center gap-4 py-3 border-t border-b border-[#262626]">
                  <button 
                    onClick={() => handleLike(selectedPost.id)}
                    className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#FF5F1F] transition-colors"
                  >
                    <Heart className={`w-5 h-5 ${selectedPost.likes?.includes(user?.id) ? 'fill-[#FF5F1F] text-[#FF5F1F]' : ''}`} />
                    <span>{selectedPost.likes?.length || 0} {t('community.likes') || 'likes'}</span>
                  </button>
                  <div className="flex items-center gap-2 text-[#9CA3AF]">
                    <MessageCircle className="w-5 h-5" />
                    <span>{comments.length} {t('community.comments') || 'commenti'}</span>
                  </div>
                </div>

                {/* Comments Section */}
                <div className="space-y-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-[#FF5F1F]" />
                    {t('community.comments') || 'Commenti'}
                  </h3>

                  {/* Comment Form */}
                  <form onSubmit={handleSubmitComment} className="flex gap-2">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={t('community.writeComment') || 'Scrivi un commento...'}
                      className="flex-1 bg-[#121212] border-[#262626] text-white"
                    />
                    <Button 
                      type="submit" 
                      disabled={submittingComment || !newComment.trim()}
                      className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
                    >
                      {submittingComment ? '...' : t('common.send') || 'Invia'}
                    </Button>
                  </form>

                  {/* Comments List */}
                  {loadingComments ? (
                    <div className="text-center py-4 text-[#9CA3AF]">
                      {t('common.loading')}
                    </div>
                  ) : comments.length === 0 ? (
                    <p className="text-[#6B7280] text-center py-4">
                      {t('community.noComments') || 'Nessun commento. Sii il primo!'}
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {comments.map((comment) => (
                        <div key={comment.id} className="bg-[#121212] border border-[#262626] p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-white font-medium text-sm">{comment.user_nome}</span>
                                <span className="text-[#6B7280] text-xs">{formatCommentDate(comment.created_at)}</span>
                              </div>
                              <p className="text-[#d4d4d4] text-sm">{comment.testo}</p>
                            </div>
                            {comment.user_id === user?.id && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-red-500 hover:text-red-400 ml-2"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
